// src/feature-module/tenants/Tenant360.tsx
//
// Platform-admin 360 view of a single tenant: header + lifecycle actions, and a
// tabbed set of the tenant's sub-resources, each lazily loaded from the backend
// /admin/tenants/:id/* endpoints via RTK Query (cached, auto-refreshed).

import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card, Tabs, Table, Tag, Button, Descriptions, Spin, Popconfirm, Space, message, Empty,
  Modal, Input, Alert, Typography,
} from "antd";
import {
  useGetTenantQuery,
  useGetTenantStoresQuery,
  useGetTenantWarehousesQuery,
  useGetTenantUsersQuery,
  useGetTenantCustomersQuery,
  useGetTenantBusinessEntitiesQuery,
  useGetTenantSuppliersQuery,
  useGetTenantProductSuppliersQuery,
  useGetTenantInventoriesQuery,
  useGetTenantTransactionsQuery,
  useGetTenantPaymentsQuery,
  useGetTenantPosSessionsQuery,
  useGetTenantHealthQuery,
  useGetTenantInvoicesQuery,
  useGetTenantPurchaseOrdersQuery,
  useGetTenantQuotationsQuery,
  useGetTenantShipmentsQuery,
  useGetTenantStockTransfersQuery,
  useGetTenantB2bConnectionsQuery,
  useGetTenantSettingsQuery,
  useGetTenantActivityQuery,
  useGetTenantErrorsQuery,
  useDeactivateTenantMutation,
  useReactivateTenantMutation,
  useLazyGetTenantArchiveImpactQuery,
  useArchiveTenantMutation,
  useRestoreTenantMutation,
} from "../../core/redux/services/adminTenantApi";
import type { TenantArchiveImpact } from "../../core/redux/services/adminTenantApi";
import SupportActions from "../support/SupportActions";
import StockDriftFix from "../support/StockDriftFix";
import { getApiErrorMessageWithRef } from "../../core/utils/apiError";

// Infer up to `max` display columns from the first row (skips nested objects/arrays,
// which render as compact JSON). Keeps the generic tables readable without per-
// resource column definitions.
function inferColumns(rows: any[], max = 6) {
  const first = rows?.[0];
  if (!first || typeof first !== "object") return [];
  const keys = Object.keys(first).filter((k) => {
    const v = first[k];
    return v === null || ["string", "number", "boolean"].includes(typeof v);
  });
  return keys.slice(0, max).map((k) => ({
    title: k,
    dataIndex: k,
    ellipsis: true,
    render: (v: any) => {
      if (v === null || v === undefined) return <span className="text-muted">—</span>;
      if (typeof v === "boolean") return <Tag color={v ? "green" : "default"}>{String(v)}</Tag>;
      if (typeof v === "string" && /\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString();
      return String(v);
    },
  }));
}

const ResourceTab: React.FC<{ useHook: any; tenantId: string }> = ({ useHook, tenantId }) => {
  const { data = [], isFetching, isError } = useHook(tenantId);
  const rows: any[] = Array.isArray(data) ? data : (data as any)?.data ?? [];
  if (isFetching) return <div className="text-center py-4"><Spin /></div>;
  if (isError) return <Empty description="Couldn't load this resource" />;
  return (
    <Table
      rowKey={(r: any) => r.id ?? r._id ?? JSON.stringify(r).slice(0, 40)}
      dataSource={rows}
      columns={inferColumns(rows)}
      loading={isFetching}
      size="small"
      scroll={{ x: "max-content" }}
      pagination={{ pageSize: 10, showSizeChanger: true }}
      locale={{ emptyText: "No records" }}
    />
  );
};

/**
 * The integrity + activity summary a supporter reads before anything else.
 *
 * `fail` is a violated invariant (something is genuinely wrong), `warn` is
 * worth knowing but not broken — chiefly historical debt like stock imported
 * before the ledger was enforced. Keeping those apart is what stops the panel
 * becoming noise people learn to scroll past.
 */
const STATUS_COLOR: Record<string, string> = { ok: "green", warn: "gold", fail: "red", error: "purple" };

const HealthPanel: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { data, isFetching, isError } = useGetTenantHealthQuery(tenantId);

  if (isFetching && !data) return <Card className="mb-3"><Spin /> Running health checks…</Card>;
  if (isError || !data) return null;

  const a = data.activity ?? {};
  return (
    <Card
      className="mb-3"
      title={
        <Space>
          <span>Health</span>
          <Tag color={STATUS_COLOR[data.overall] ?? "default"}>{String(data.overall).toUpperCase()}</Tag>
          <span className="text-muted" style={{ fontWeight: 400, fontSize: 12 }}>
            stuck threshold {data.stuckThresholdDays}d
          </span>
        </Space>
      }
    >
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} className="mb-3">
        <Descriptions.Item label="Active users">{a.activeUsers ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Activity (24h)">{a.activityLast24h ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Errors (24h / 7d)">
          {(a.errorsLast24h ?? 0)} / {(a.errorsLast7d ?? 0)}
        </Descriptions.Item>
        <Descriptions.Item label="Last activity">
          {a.lastActivity?.timestamp
            ? `${new Date(a.lastActivity.timestamp).toLocaleString()} (${a.lastActivity.module ?? "?"}/${a.lastActivity.action ?? "?"})`
            : "never"}
        </Descriptions.Item>
        <Descriptions.Item label="Live support sessions">
          {a.activeImpersonationSessions ? (
            <Tag color="orange">{a.activeImpersonationSessions}</Tag>
          ) : (
            0
          )}
        </Descriptions.Item>
      </Descriptions>

      <Table
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={data.checks ?? []}
        columns={[
          {
            title: "",
            dataIndex: "status",
            width: 80,
            render: (v: string) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v?.toUpperCase()}</Tag>,
          },
          { title: "Check", dataIndex: "label" },
          { title: "Count", dataIndex: "count", width: 80 },
          { title: "Detail", dataIndex: "detail", render: (v: string) => <span className="text-muted">{v}</span> },
        ]}
        expandable={{
          // The stock check is the one whose samples carry everything its repair
          // needs, so it gets the repair inline instead of a JSON dump. Every
          // other check still shows raw samples — an honest "here is what I
          // found" beats inventing a fix that does not exist yet.
          expandedRowRender: (r: any) =>
            r.key === "stock_vs_ledger" && r.status === "fail" ? (
              <StockDriftFix tenantId={tenantId} samples={r.samples ?? []} />
            ) : (
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>
                {JSON.stringify(r.samples ?? [], null, 2)}
              </pre>
            ),
          rowExpandable: (r: any) => Array.isArray(r.samples) && r.samples.length > 0,
        }}
      />
    </Card>
  );
};

/**
 * The console's "delete" action.
 *
 * What it replaced was a one-line Popconfirm reading "Delete this tenant and all
 * its data? This cannot be undone." Every clause of that was wrong: the backend
 * could not delete a tenant at all (a dozen tables restrict the FK, so the call
 * only ever 500'd), it would not have taken "all its data" if it had (the
 * cascades landed on the ledger and nothing else), and being unable to complete
 * is not the same as being irreversible.
 *
 * The platform archives instead — see the lifecycle note on `model Tenant`. So
 * this dialog does the opposite job of the old one: it states plainly what is
 * KEPT, names the only thing actually lost (live sessions), and asks for the
 * reason that ends up in the audit trail.
 */
const ArchiveTenantAction: React.FC<{
  tenantId: string;
  tenantName?: string;
  isSystem?: boolean;
}> = ({ tenantId, tenantName, isSystem }) => {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [confirmName, setConfirmName] = React.useState("");
  const [fetchImpact, { data: impact, isFetching }] = useLazyGetTenantArchiveImpactQuery();
  const [archive, { isLoading }] = useArchiveTenantMutation();

  const openDialog = () => {
    setReason("");
    setConfirmName("");
    setOpen(true);
    fetchImpact(tenantId);
  };

  // Reversible, but it signs every user out — enough disruption to be worth
  // confirming you are on the tenant you think you are.
  const nameMatches = !tenantName || confirmName.trim() === tenantName.trim();
  const canSubmit = reason.trim().length >= 10 && nameMatches;

  const submit = async () => {
    try {
      const res = await archive({ id: tenantId, reason: reason.trim() }).unwrap();
      message.success(res?.message || "Tenant archived. All records retained.");
      setOpen(false);
    } catch (e) {
      message.error(getApiErrorMessageWithRef(e, "Could not archive this tenant."));
    }
  };

  return (
    <>
      <Button danger onClick={openDialog} disabled={isSystem}>
        Archive tenant
      </Button>
      <Modal
        title={`Archive ${tenantName || "this tenant"}?`}
        open={open}
        onCancel={() => setOpen(false)}
        confirmLoading={isLoading}
        okText="Archive tenant"
        okButtonProps={{ danger: true, disabled: !canSubmit }}
        onOk={submit}
        width={620}
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Nothing is deleted."
          description={
            "Archiving closes the workspace: sign-in is refused and every live session " +
            "ends immediately. All records stay in the database and remain readable here. " +
            "A superadmin can restore the tenant at any time."
          }
        />

        {isFetching ? (
          <div className="text-center py-3"><Spin /></div>
        ) : impact ? (
          <ArchiveImpactSummary impact={impact} />
        ) : null}

        <div className="mt-3">
          <Typography.Text strong>Reason</Typography.Text>
          <Typography.Text type="secondary" className="ms-2">
            (required — recorded in the audit trail)
          </Typography.Text>
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Subscription ended 2026-09-30; closed at the customer's request."
            status={reason.length > 0 && reason.trim().length < 10 ? "error" : undefined}
          />
        </div>

        {tenantName ? (
          <div className="mt-3">
            <Typography.Text strong>Type the tenant name to confirm</Typography.Text>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={tenantName}
              status={confirmName.length > 0 && !nameMatches ? "error" : undefined}
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
};

/** The counts, split into what survives and what does not. */
const ArchiveImpactSummary: React.FC<{ impact: TenantArchiveImpact }> = ({ impact }) => {
  const r = impact.retained;
  const kept: [string, number][] = [
    ["Transactions", r.transactions],
    ["Invoices", r.invoices],
    ["Payments", r.payments],
    ["Journal entries", r.journalEntries],
    ["Purchase orders", r.purchaseOrders],
    ["Customers", r.customers],
    ["Users", r.users],
    ["Stores", r.stores],
    ["Warehouses", r.warehouses],
    ["Patients", r.patients],
    ["Prescriptions", r.prescriptions],
  ].filter(([, n]) => n > 0) as [string, number][];

  return (
    <>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Signed out immediately">
          {impact.revocable.activeSessions} active session
          {impact.revocable.activeSessions === 1 ? "" : "s"}
        </Descriptions.Item>
        <Descriptions.Item label="Retained (not deleted)">
          {kept.length === 0 ? (
            <Typography.Text type="secondary">
              This tenant has no records — nothing to retain.
            </Typography.Text>
          ) : (
            <Space size={[4, 4]} wrap>
              {kept.map(([label, n]) => (
                <Tag key={label} color="green">{label}: {n.toLocaleString()}</Tag>
              ))}
            </Space>
          )}
        </Descriptions.Item>
      </Descriptions>

      {impact.counterparty.b2bPartners > 0 ? (
        <Alert
          type="warning"
          showIcon
          className="mt-3"
          message={`Shared with ${impact.counterparty.b2bPartners} B2B partner${
            impact.counterparty.b2bPartners === 1 ? "" : "s"
          }`}
          description={
            "This tenant appears in other tenants' records as a trading counterparty. " +
            "Those partners keep their own history; archiving does not touch it, and " +
            "their existing connections will show this tenant as closed."
          }
        />
      ) : null}
    </>
  );
};

const Tenant360: React.FC = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: tenant, isLoading } = useGetTenantQuery(id, { skip: !id });
  const [deactivate, { isLoading: deactivating }] = useDeactivateTenantMutation();
  const [reactivate, { isLoading: reactivating }] = useReactivateTenantMutation();
  const [restore, { isLoading: restoring }] = useRestoreTenantMutation();

  // Three lifecycle states, not two: live, suspended, archived. `deletedAt` is
  // the terminal one and outranks isActive — an archived tenant is always
  // inactive, so showing both as separate toggles would be noise.
  const isArchived = !!tenant?.deletedAt;
  const isActive = !isArchived && (tenant?.isActive ?? tenant?.status !== "INACTIVE");

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      message.success(ok);
    } catch (e) {
      // Was a bare "Action failed", which threw away both the server's reason
      // and the correlation id — on the one screen whose users resolve
      // correlation ids for a living.
      message.error(getApiErrorMessageWithRef(e, "Action failed."));
    }
  };

  const tabs = [
    { key: "stores", label: "Stores", useHook: useGetTenantStoresQuery },
    { key: "warehouses", label: "Warehouses", useHook: useGetTenantWarehousesQuery },
    { key: "users", label: "Users", useHook: useGetTenantUsersQuery },
    { key: "customers", label: "Customers", useHook: useGetTenantCustomersQuery },
    { key: "entities", label: "Business Entities", useHook: useGetTenantBusinessEntitiesQuery },
    { key: "suppliers", label: "Suppliers", useHook: useGetTenantSuppliersQuery },
    { key: "product-suppliers", label: "Product Suppliers", useHook: useGetTenantProductSuppliersQuery },
    { key: "inventories", label: "Inventory", useHook: useGetTenantInventoriesQuery },
    { key: "transactions", label: "Transactions", useHook: useGetTenantTransactionsQuery },
    { key: "payments", label: "Payments", useHook: useGetTenantPaymentsQuery },
    { key: "pos-sessions", label: "POS Sessions", useHook: useGetTenantPosSessionsQuery },
    // The domains support is actually called about. These hang off the
    // resources' own /{resource}/tenant/:id routes, which already existed.
    { key: "invoices", label: "Invoices", useHook: useGetTenantInvoicesQuery },
    { key: "purchase-orders", label: "Purchase Orders", useHook: useGetTenantPurchaseOrdersQuery },
    { key: "quotations", label: "Quotations", useHook: useGetTenantQuotationsQuery },
    { key: "stock-transfers", label: "Stock Transfers", useHook: useGetTenantStockTransfersQuery },
    { key: "shipments", label: "Shipments", useHook: useGetTenantShipmentsQuery },
    { key: "b2b", label: "B2B", useHook: useGetTenantB2bConnectionsQuery },
    { key: "settings", label: "Settings", useHook: useGetTenantSettingsQuery },
    // "What happened" and "what went wrong", both keyed on requestId so a
    // correlation id from a tenant's error joins the two.
    { key: "activity", label: "Activity", useHook: useGetTenantActivityQuery },
    { key: "errors", label: "Errors", useHook: useGetTenantErrorsQuery },
  ].map((t) => ({
    key: t.key,
    label: t.label,
    children: <ResourceTab useHook={t.useHook} tenantId={id} />,
  }));

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">{tenant?.name || "Tenant"}</h4>
            <Link to="/tenants" className="small">← Back to tenants</Link>
          </div>
          <Space wrap>
            {/* Support actions sit BEFORE the destructive lifecycle buttons:
                they are what a supporter reaches for, and they are the safe
                ones. Deactivate/Delete stay at the far end. */}
            <SupportActions tenantId={id as string} tenantName={tenant?.name} tenantType={tenant?.type} />
            {isArchived ? (
              // Archived is terminal, so the only lifecycle move left is back out
              // of it. Suspend/resume would be meaningless here.
              <Popconfirm
                title="Restore this tenant?"
                description="Sign-in is re-enabled and the tenant becomes active again."
                onConfirm={() => act(() => restore(id).unwrap(), "Tenant restored")}
              >
                <Button type="primary" loading={restoring}>Restore tenant</Button>
              </Popconfirm>
            ) : (
              <>
                {isActive ? (
                  <Popconfirm
                    title="Suspend this tenant?"
                    description="Sign-in is refused and live sessions end. Reversible; no data is touched."
                    onConfirm={() => act(() => deactivate(id).unwrap(), "Tenant deactivated")}
                  >
                    <Button danger loading={deactivating}>Deactivate</Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" loading={reactivating} onClick={() => act(() => reactivate(id).unwrap(), "Tenant reactivated")}>
                    Reactivate
                  </Button>
                )}
                <ArchiveTenantAction
                  tenantId={id as string}
                  tenantName={tenant?.name}
                  isSystem={tenant?.isSystem}
                />
              </>
            )}
          </Space>
        </div>

        <Card className="mb-3">
          {isLoading ? (
            <div className="text-center py-4"><Spin /></div>
          ) : (
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Name">{tenant?.name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Type">{tenant?.type || "—"}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {isArchived ? (
                  <Space direction="vertical" size={2}>
                    <Tag color="default">Archived</Tag>
                    <Typography.Text type="secondary" className="small">
                      {new Date(tenant!.deletedAt as string).toLocaleString()}
                      {tenant?.deletionReason ? ` — ${tenant.deletionReason}` : ""}
                    </Typography.Text>
                  </Space>
                ) : (
                  <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Suspended"}</Tag>
                )}
              </Descriptions.Item>
              {/* KYC state. Deferred until the backend included the relation on
                  the tenant read — it does now, so this needs no second call.
                  Verification status belongs beside Status: a tenant can be
                  ACTIVE and still unverified, and once the KYC gates are switched
                  on that is the difference between trading and being blocked. */}
              <Descriptions.Item label="KYC">
                {(() => {
                  const kyc = (tenant as any)?.kyc;
                  if (!kyc) return <Tag>Not started</Tag>;
                  const status = String(kyc.status ?? "").toUpperCase();
                  const color =
                    status === "APPROVED" ? "green" :
                    status === "REJECTED" ? "red" :
                    status === "SUBMITTED" || status === "UNDER_REVIEW" ? "gold" : "default";
                  return (
                    <Space size={4} wrap>
                      <Tag color={color}>{status || "UNKNOWN"}</Tag>
                      {kyc.expiresAt && (
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          expires {new Date(kyc.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </Space>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Contact">{tenant?.contactEmail || "—"}</Descriptions.Item>
              <Descriptions.Item label="Slug">{tenant?.slug || "—"}</Descriptions.Item>
              <Descriptions.Item label="Created">
                {tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : "—"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <HealthPanel tenantId={id as string} />

        <Card>
          <Tabs items={tabs} destroyInactiveTabPane />
        </Card>
      </div>
    </div>
  );
};

export default Tenant360;
